import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Home } from './pages/Home';
import { Overview } from './pages/Overview';
import { ChaletDetail } from './pages/ChaletDetail';
import { Checkout } from './pages/Checkout';
import { Confirmation } from './pages/Confirmation';
import { Admin } from './pages/Admin';
import { Contact } from './pages/Contact';
import { Rent } from './pages/Rent';
import { Login } from './pages/Login';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const GermanFlag = () => (
  <svg
    viewBox="0 0 5 3"
    className="inline-block w-5 h-3.5 rounded-[2px] shadow-sm ring-1 ring-black/10"
    aria-hidden="true"
  >
    <rect width="5" height="1" y="0" fill="#000" />
    <rect width="5" height="1" y="1" fill="#DD0000" />
    <rect width="5" height="1" y="2" fill="#FFCE00" />
  </svg>
);

// Main content wrapper to handle the transition effect
const MainContent: React.FC = () => {
    const { isTransitioning: isLangTransitioning } = useLanguage();
    const location = useLocation();
    const [isPageChanging, setIsPageChanging] = useState(false);

    useEffect(() => {
        setIsPageChanging(true);
        const timer = setTimeout(() => setIsPageChanging(false), 400);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    // Combine language transition and page change transition
    const isActuallyTransitioning = isLangTransitioning || isPageChanging;
    
    return (
        <div className={`transition-all duration-500 ease-out min-h-[70vh] ${
            isActuallyTransitioning 
            ? 'opacity-0 translate-y-4' 
            : 'opacity-100 translate-y-0'
        }`}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/overview" element={<Overview />} />
                <Route path="/chalet/:id" element={<ChaletDetail />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/confirmation" element={<Confirmation />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/verhuren" element={<Rent />} />
                <Route path="/login" element={<Login />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
            </Routes>
        </div>
    );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <HashRouter>
        <ScrollToTop />
        <div className="min-h-screen font-sans flex flex-col">
          <Navbar />
          <div className="flex-grow">
             <MainContent />
          </div>
          
          <footer className="bg-brand-900 text-brand-100 py-12 mt-auto">
              <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
                  <div>
                      <span className="font-serif text-xl font-bold text-white mb-4 block">Chalet Inn</span>
                      <p className="opacity-70">Chalet Inn heeft de mooiste chalets in de natuur en voor mensen die opzoek zijn naar rust.</p>
                  </div>
                  <div>
					<h4 className="font-bold text-white mb-4">Provincies</h4>
					<ul className="space-y-2 opacity-70">
					  <li><Link to="/overview" state={{ location: 'Drenthe' }}>Drenthe</Link></li>
					  <li><Link to="/overview" state={{ location: 'Flevoland' }}>Flevoland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Friesland' }}>Friesland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Gelderland' }}>Gelderland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Groningen' }}>Groningen</Link></li>
					  <li><Link to="/overview" state={{ location: 'Limburg' }}>Limburg</Link></li>
					  <li><Link to="/overview" state={{ location: 'Noord-Brabant' }}>Noord-Brabant</Link></li>
					  <li><Link to="/overview" state={{ location: 'Noord-Holland' }}>Noord-Holland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Overijssel' }}>Overijssel</Link></li>
					  <li><Link to="/overview" state={{ location: 'Utrecht' }}>Utrecht</Link></li>
					  <li><Link to="/overview" state={{ location: 'Zeeland' }}>Zeeland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Zuid-Holland' }}>Zuid-Holland</Link></li>
					  <li><Link to="/overview" state={{ location: 'Duitsland' }} className="inline-flex items-center gap-2"><GermanFlag />Duitsland</Link></li>
					</ul>
                  </div>
                  <div>
                      <h4 className="font-bold text-white mb-4">Informatie</h4>
                      <ul className="space-y-2 opacity-70">
                          <li><Link to="/blog">Blog</Link></li>
                          <li><Link to="/contact">Contact</Link></li>
                          <li><Link to="/verhuren">Uw huis verhuren?</Link></li>
                          <li><a href="https://chaletinn.nl/docs/Algemene_Voorwaarden_Vakantieverhuur_Chalet_Inn_nl.pdf" target="_blank" rel="noopener noreferrer">Algemene voorwaarden</a></li>
                      </ul>
					<div class="trustpilot-widget mt-10" data-locale="nl-NL" data-template-id="56278e9abfbbba0bdcd568bc" data-businessunit-id="6999b7db41462e7a091fea82" data-style-height="52px" data-style-width="100%" data-token="dcdfe970-82d2-4910-a77d-4d71eafeff81">
					  <a href="https://nl.trustpilot.com/review/chaletinn.nl" target="_blank" rel="noopener">Trustpilot</a>
					</div>					  
					  
                  </div>
              </div>
              <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-brand-800 text-center opacity-50 text-xs">
                  &copy; {new Date().getFullYear()} Chalet Inn
              </div>
          </footer>
        </div>
      </HashRouter>
    </LanguageProvider>
  );
};

export default App;